import { Form, Input, Steps, message } from 'antd';
import { useEffect, useState } from 'react';

import { useHistory } from 'react-router';
import { TabContentWrapper } from '@/components/_common';
import { RouteType } from '@/types/RouteType';
import { EmailMontageFormValues } from './EmailMontagePage';
import {
  CrmSendOptionCommunication,
  SendOptionType,
} from '@/core/domain/crm/SendOptions/SendOptions.types';
import { useRouteMatchParam, useWindowSize } from '@/hooks';
import {
  useCrmSendOptionCommunication,
  useCrmSendOptionCommunicationWithBilling,
  useCrmSendOptionCommunicationWithVehicle,
  useCrmSendOptionFutureServiceCommunication,
  useCrmSendOptionNextReviewCommunication,
  useDetailSendOption,
  useEditSendOption,
} from '@/core/domain/crm/SendOptions/SendOptions.hooks';
import ShippingOptionsEmailForm from '@/components/CRM/ShippingOptions/ShippingOptionsEmailForm';
import CrmCustomersList from '@/components/CRM/ShippingOptions/CustomerList/CrmCustomersList';
import ShippingOptionsSumarySend from '@/components/CRM/ShippingOptions/ShippingOptionsSumarySend';
import mailBase64ToUrl from '@/utils/functions/mailBase64ToUrl';
import { CrmReceiverCustomer } from '@/components/CRM/ShippingOptions/ShippingOptionsCustomerTable';
import CrmCustomersWithVehicleList from '@/components/CRM/ShippingOptions/CustomerList/CustomerWithVehicleList';
import CrmCustomersDebtsList from '@/components/CRM/ShippingOptions/CustomerList/CrmCustomersDebtsList';
import CrmCustomersFutureServicesList from '@/components/CRM/ShippingOptions/CustomerList/CrmCustomersFutureServicesList';
import CrmCustomersNextRevisionsList from '@/components/CRM/ShippingOptions/CustomerList/CrmCustomersNextRevisions';

interface Props {
  route: RouteType;
}

interface PageParams {
  step?: string;
  type?: SendOptionType;
}

const typeWithVehicles = [SendOptionType.custom, SendOptionType.emailMarketing];

const ShippingOptionsManager: React.FC<Props> = ({ route }) => {
  const { isMobile } = useWindowSize();
  const history = useHistory();
  const sendOptionId = useRouteMatchParam('sendOptionId');
  const [customers, setCustomers] = useState<CrmReceiverCustomer[]>([]);

  const [uploading, setUploading] = useState(false);

  const [form] = Form.useForm<EmailMontageFormValues>();
  const [formValues, setFormValues] =
    useState<Partial<EmailMontageFormValues>>();

  const [currentStep, setCurrentStep] = useState(0);
  const [shippingType, setShippingType] = useState<SendOptionType>();

  const sendOptionCommunicationWithBilling =
    useCrmSendOptionCommunicationWithBilling();
  const sendOptionCommunication = useCrmSendOptionCommunication();
  const sendOptionCommunicationWithVehicle =
    useCrmSendOptionCommunicationWithVehicle();
  const sendOptionEdit = useEditSendOption();
  const futureServiceCommunication =
    useCrmSendOptionFutureServiceCommunication();
  const nextReviewCommunication = useCrmSendOptionNextReviewCommunication();

  const { mutateAsync: getSendOption, data: sendOption } =
    useDetailSendOption();

  useEffect(() => {
    if (!formValues) {
      handleSendOption();
    }
  }, [sendOptionId]);

  async function handleSendOption() {
    const result = await getSendOption(Number(sendOptionId));
    form.setFieldsValue(result);
    setFormValues(result);
  }
  const steps = [
    {
      key: 'email',
      title: 'Montagem de e-mail',
      content: (
        <ShippingOptionsEmailForm
          shippingType={shippingType}
          shippingOption={sendOption}
          loading={sendOptionEdit.isLoading || uploading}
        />
      ),
    },
    {
      key: 'customers',
      title: 'Clientes',
      content:
        shippingType && typeWithVehicles.includes(shippingType) ? (
          <CrmCustomersWithVehicleList
            onPrev={onPrev}
            CustomersSelected={customers}
            onCustomersSelected={setCustomers}
            customerIds={customers.map((item) => item.id)}
          />
        ) : shippingType === SendOptionType.recoveryOfDebts ? (
          <CrmCustomersDebtsList
            onPrev={onPrev}
            CustomersSelected={customers}
            onCustomersSelected={setCustomers}
            customerIds={customers.map((item) => item.id)}
          />
        ) : shippingType === SendOptionType.futureServices ? (
          <CrmCustomersFutureServicesList
            onPrev={onPrev}
            CustomersSelected={customers}
            onCustomersSelected={setCustomers}
            customerIds={customers.map((item) => item.id)}
          />
        ) : shippingType === SendOptionType.nextReviews ? (
          <CrmCustomersNextRevisionsList
            onPrev={onPrev}
            CustomersSelected={customers}
            onCustomersSelected={setCustomers}
            customerIds={customers.map((item) => item.id)}
          />
        ) : (
          <CrmCustomersList
            onPrev={onPrev}
            CustomersSelected={customers}
            onCustomersSelected={setCustomers}
            customerIds={customers.map((item) => item.id)}
          />
        ),
    },
    {
      key: 'finalize',
      title: 'Finalizar',
      content: (
        <ShippingOptionsSumarySend
          onPrev={onPrev}
          loading={
            sendOptionCommunication.isLoading ||
            sendOptionCommunicationWithVehicle.isLoading ||
            sendOptionCommunicationWithBilling.isLoading
          }
          customers={customers}
        />
      ),
    },
  ];

  useEffect(() => {
    const params: PageParams = Object.fromEntries(
      new URLSearchParams(history.location.search),
    );

    if (params.step) {
      setCurrentStep(Number(params.step));
    }

    if (params.type) {
      setShippingType(params.type);
    }
  }, [history.location.search]);

  async function onPrev() {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);

      const params = new URLSearchParams(history.location.search);
      params.set('step', String(currentStep - 1));

      history.push(`${history.location.pathname}?${params.toString()}`);
    }
  }

  async function onNext() {
    try {
      if (currentStep === 0 && sendOption && shippingType) {
        const values = form.getFieldsValue();
        let newBody = '';

        newBody = await mailBase64ToUrl(values.body, setUploading);

        await sendOptionEdit.mutateAsync({
          optionId: sendOption?.id,
          data: {
            ...values,
            body: newBody,
            type: shippingType,
            matrixId: sendOption.matrixId,
          },
        });

        form.setFieldValue('body', newBody);
        setFormValues((state) => ({ ...state, body: newBody }));
      }

      if (currentStep === 1 && !customers.length) {
        return message.warning('Selecione pelo menos um item da lista.');
      }

      if (currentStep < 2) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);

        const params = new URLSearchParams(history.location.search);
        params.set('step', String(nextStep));

        history.push(`${history.location.pathname}?${params.toString()}`);
      } else if (shippingType && formValues) {
        const { companyId, ...values } = formValues as EmailMontageFormValues;

        const obj = {
          sendOption: {
            type: shippingType,
            systemDefaultType: !!values.systemDefaultType,
            id: sendOption?.id,
            body: values.body,
            quizId: values.quizId,
            subject: values.subject,
            title: values.title,
          },
          companyId,
        } as CrmSendOptionCommunication;

        const customersWithEmail = customers.filter(
          (item) => !!item.customer.email,
        );

        if (shippingType === SendOptionType.recoveryOfDebts) {
          await sendOptionCommunicationWithBilling.mutateAsync({
            ...obj,
            billingComunications: customersWithEmail.map((item) => ({
              customerId: item.customer.id,
              email: item.customer.email,
              billsIds: Number(item.id),
            })),
          });
        } else if (typeWithVehicles.includes(shippingType)) {
          await sendOptionCommunicationWithVehicle.mutateAsync({
            ...obj,
            comunicationsWithVehicles: customersWithEmail.map((item) => ({
              customerId: item.customer.id,
              email: item.customer.email,
              vehicleId: Number(item.vehicle?.id),
            })),
          });
        } else if (shippingType === SendOptionType.futureServices) {
          await futureServiceCommunication.mutateAsync({
            ...obj,
            futureServicesComunications: customersWithEmail.map((item) => ({
              futuresServicesIds:
                item.futureServices?.map((service) => service.id) || [],
              customerId: item.customer.id,
              email: item.customer.email,
              vehicleId: Number(item.id),
            })),
          });
        } else if (shippingType === SendOptionType.nextReviews) {
          await nextReviewCommunication.mutateAsync({
            ...obj,
            nextReviewsComunications: customersWithEmail.map((item) => ({
              vehicleItensIds: item.itens?.map((item) => item.id) || [],
              customerId: item.customer.id,
              email: item.customer.email,
              vehicleId: Number(item.id),
            })),
          });
        } else if (shippingType === SendOptionType.satisfactionSurvey) {
          await sendOptionCommunicationWithVehicle.mutateAsync({
            ...obj,
            comunicationsWithVehicles: customersWithEmail.map((item) => ({
              customerId: item.customer.id,
              email: item.customer.email,
              vehicleId: item.customer?.vehicle?.id,
            })),
          });
        } else {
          await sendOptionCommunication.mutateAsync({
            ...obj,
            comunications: customersWithEmail.map(
              (item: CrmReceiverCustomer) => ({
                customerId: item.customer.id,
                email: item.customer.email,
                vehicleId: item.customer?.vehicle?.id,
              }),
            ),
          });
        }

        setCurrentStep(0);

        const params = new URLSearchParams(history.location.search);
        params.set('step', String(0));

        history.push(`${history.location.pathname}?${params.toString()}`);
      }
    } catch {}
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={onNext}
        onValuesChange={(_, fields) =>
          setFormValues((state) => ({ ...state, ...fields }))
        }
      >
        <Form.Item name="systemDefaultType" hidden>
          <Input />
        </Form.Item>

        <TabContentWrapper title={route.name} permissions={route.permissions}>
          <div>
            <Steps
              direction={isMobile ? 'vertical' : 'horizontal'}
              size={isMobile ? 'small' : 'default'}
              current={currentStep}
            >
              {steps.map((step) => (
                <Steps.Step key={step.key} title={step.title} />
              ))}
            </Steps>

            <div style={{ margin: '24px 0' }}>{steps[currentStep].content}</div>
          </div>
        </TabContentWrapper>
      </Form>
    </>
  );
};

export default ShippingOptionsManager;
